import ModalCloseIcon from '@cointracker/styleguide/src/icons/close-modal.svg?react';
import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';
import { useState } from 'react';
import { Grid } from '../Grid';
import { Button } from '../LoggedIn/Button';
import {
  FilterSelectItem,
  InlineSelect,
  InlineSelectItem,
  Select,
} from '../Select';
import {
  FilterSelect,
  FilterSelectButton,
  FilterSelectItemRaw,
} from '../Select/FilterSelect';
import { SelectItem } from '../Select/Select';
import { Body1, H6 } from '../typography';
import {
  dropdown,
  filterMenu,
  filterMenuBelowDesktop,
  heading,
  modalBody,
  modalCloseButton,
  modalContent,
  modalContentInner,
  modalFooter,
  modalHeader,
  modalSectionHeading,
  modalSyncMethodsItems,
  root,
} from './IntegrationsFilterMenu.css';
import {
  IntegrationsCategoryOption,
  IntegrationsSortByOption,
  categoryLabels,
  sortByLabels,
  type IntegrationsFilter,
} from './utils';

export interface IntegrationsFilterMenuProps {
  className?: string;
  count: number;
  csvInfoHref: string;
  filter: IntegrationsFilter;
  onChange?: (filter: IntegrationsFilter) => void;
}

export const IntegrationsFilterMenu = (props: IntegrationsFilterMenuProps) => {
  const { className, count, csvInfoHref, filter, onChange, ...rest } = props;

  const [autoSync, setAutoSync] = useState(filter.autoSync);
  const [customFile, setCustomFile] = useState(filter.customFile);
  const [csv, setCsv] = useState(filter.csv);

  const sendOnChange = (overrides?: Partial<IntegrationsFilter>) => {
    onChange?.({
      sortBy: filter.sortBy,
      category: filter.category,
      autoSync,
      customFile,
      csv,
      ...overrides,
    });
  };

  const onChangeSortBy = (value: IntegrationsSortByOption) => {
    sendOnChange({ sortBy: value });
  };

  const onChangeCategory = (value: IntegrationsCategoryOption) => {
    sendOnChange({ category: value });
  };

  const resetSync = () => {
    setAutoSync(filter.autoSync);
    setCustomFile(filter.customFile);
    setCsv(filter.csv);
  };

  return (
    <Grid className={classNames(root, className)} {...rest}>
      <div className={heading}>
        <H6>Integrations</H6>
        <H6 variant="secondary">{count}</H6>
      </div>
      <div className={filterMenu}>
        <Select
          value={filter.sortBy}
          onValueChange={onChangeSortBy}
          className={dropdown}
        >
          {Object.entries(sortByLabels).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {'Sort by ' + label}
            </SelectItem>
          ))}
        </Select>
        <Select
          value={filter.category}
          onValueChange={onChangeCategory}
          className={dropdown}
        >
          {Object.entries(categoryLabels).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </Select>
        <FilterSelect
          value="Sync Methods"
          className={dropdown}
          onOpenChange={resetSync}
        >
          <FilterSelectItem
            title="Automatic sync"
            checked={autoSync}
            onCheckedChange={setAutoSync}
          >
            Just add your API keys or public address, and CoinTracker will
            automatically sync your transactions. All wallet and blockchain
            integrations support automatic sync.
          </FilterSelectItem>
          <FilterSelectItem
            title="Custom file"
            checked={customFile}
            onCheckedChange={setCustomFile}
          >
            Upload a transaction history file from your exchange and CoinTracker
            will directly import your transactions.
          </FilterSelectItem>
          <FilterSelectItem
            title="CoinTracker CSV"
            checked={csv}
            onCheckedChange={setCsv}
          >
            Import any transactions to CoinTracker by uploading them in the
            CoinTracker CSV format.{' '}
            <a href={csvInfoHref} className="underline">
              More&nbsp;info
            </a>
          </FilterSelectItem>
          <FilterSelectButton onClick={() => sendOnChange()} fluid>
            Filter
          </FilterSelectButton>
        </FilterSelect>
      </div>
      <div className={filterMenuBelowDesktop}>
        <Dialog.Root>
          <Dialog.Trigger asChild>
            <Button variant="line" size="medium">
              Filter&nbsp;+
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content asChild>
              <div className={modalContent}>
                <Grid className={modalContentInner}>
                  <div className={modalHeader}>
                    <H6>Filter integrations</H6>
                    <Dialog.Close
                      className={modalCloseButton}
                      onClick={() => sendOnChange()}
                    >
                      <ModalCloseIcon />
                    </Dialog.Close>
                  </div>
                  <div className={modalBody}>
                    <div>
                      <Body1 weight="bold" className={modalSectionHeading}>
                        Sort by
                      </Body1>
                      <InlineSelect
                        value={filter.sortBy}
                        onValueChange={onChangeSortBy}
                      >
                        {Object.entries(sortByLabels).map(([value, label]) => (
                          <InlineSelectItem key={value} value={value}>
                            {label}
                          </InlineSelectItem>
                        ))}
                      </InlineSelect>
                    </div>
                    <div>
                      <Body1 weight="bold" className={modalSectionHeading}>
                        Category
                      </Body1>
                      <InlineSelect
                        value={filter.category}
                        onValueChange={onChangeCategory}
                      >
                        {Object.entries(categoryLabels).map(
                          ([value, label]) => (
                            <InlineSelectItem key={value} value={value}>
                              {label}
                            </InlineSelectItem>
                          ),
                        )}
                      </InlineSelect>
                    </div>
                    <div>
                      <Body1 weight="bold" className={modalSectionHeading}>
                        Sync methods
                      </Body1>
                      <div className={modalSyncMethodsItems}>
                        <FilterSelectItemRaw
                          title="Automatic sync"
                          checked={autoSync}
                          onCheckedChange={setAutoSync}
                        >
                          Just add your API keys or public address, and
                          CoinTracker will automatically sync your transactions.
                          All wallet and blockchain integrations support
                          automatic sync.
                        </FilterSelectItemRaw>
                        <FilterSelectItemRaw
                          title="Custom file"
                          checked={customFile}
                          onCheckedChange={setCustomFile}
                        >
                          Upload a transaction history file from your exchange
                          and CoinTracker will directly import your
                          transactions.
                        </FilterSelectItemRaw>
                        <FilterSelectItemRaw
                          title="CoinTracker CSV"
                          checked={csv}
                          onCheckedChange={setCsv}
                        >
                          Import any transactions to CoinTracker by uploading
                          them in the CoinTracker CSV format.{' '}
                          <a href={csvInfoHref} className="underline">
                            More&nbsp;info
                          </a>
                        </FilterSelectItemRaw>
                      </div>
                    </div>
                  </div>
                  <div className={modalFooter}>
                    <Dialog.Close asChild onClick={() => sendOnChange()}>
                      <FilterSelectButton fluid>Filter</FilterSelectButton>
                    </Dialog.Close>
                  </div>
                </Grid>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </Grid>
  );
};

IntegrationsFilterMenu.displayName = 'IntegrationsFilterMenu';
